import security from './security';
import globals from './globals';
import config from './config';
import CustomMessageBox from './CustomMessageBox';

const $ = window.$;
// Only the choice is shared. Each live operation retains its own inputs,
// completion and current stage. Nothing is persisted or queued for later login.
let choice = null;
const cancellation = () => ({ cancelled: true });
const recoverable = error => error && !error.cancelled && !error.permissionDenied &&
    !error.authenticationFailed && !!error.retryable;

function wait(error, retryStarted = () => {}) {
    const result = $.Deferred();
    const retryAt = error?.throttled ? Date.now() + (error.retryAfterSeconds || 60) * 1000 : 0;
    let timer;
    result.retry = () => {
        retryStarted();
        const delay = retryAt - Date.now();
        if (delay > 0) timer = setTimeout(() => result.resolve(), delay);
        else result.resolve();
    };
    if (!recoverable(error)) return result.reject(error).promise();
    if (!choice) {
        const entry = { waiters: new Set(), dialog: null };
        choice = entry;
        const retry = config.getTranslationText('renewal.retry');
        const message = new CustomMessageBox(config.getMessageText('1571', globals.getActiveCultureCodeForTranslations()),
            config.applicationTitle, [retry, config.getTranslationText('65')], config.getMessageReference('1571'))
            .setModalDisplayMode(config.MODAL_DISPLAY_MODE.OVERLAY);
        message.addCallback(button => {
            if (choice !== entry) return;
            choice = null;
            for (const waiter of [...entry.waiters]) {
                if (String(button).trim() === retry) waiter.retry();
                else waiter.reject(cancellation());
            }
        });
        entry.dialog = globals.showDialog(message);
    }
    const entry = choice;
    entry.waiters.add(result);
    result.always(() => {
        clearTimeout(timer);
        entry.waiters.delete(result);
        if (!entry.waiters.size && choice === entry) {
            choice = null;
            entry.dialog?.cancel?.();
        }
    });
    const promise = result.promise();
    promise.cancel = () => result.reject(cancellation());
    return promise;
}

function run(action, options = {}) {
    const result = $.Deferred(), owner = security.getIncarnation();
    let authority = security.captureProof?.();
    const stages = new Set();
    let showingBusy = false;
    const owned = () => result.state() === 'pending' && security.incarnationMatches(owner) &&
        (!options.isCurrent || options.isCurrent());
    // Only a failure path may supply its error. A terminal cookie denial
    // revokes authority itself, but still belongs to its live initiating caller.
    // Success/dispatch/cache checks call current() and retain strict admission.
    const current = error => owned() && (!authority || security.sameAuthority(authority) || security.isCurrentRenewalDenial?.(authority, error));
    const busy = value => {
        if (showingBusy === value) return;
        showingBusy = value;
        options.busy?.(value);
        if (options.spinner !== false) {
            globals.sessionOperationCount = (globals.sessionOperationCount || 0) + (value ? 1 : -1);
            if (globals.sessionOperationCount) globals.showSpinner(); else globals.hideSpinner();
        }
    };
    const cancel = () => result.reject(cancellation());
    const release = security.registerCancellation(cancel);
    const promise = result.promise();
    promise.cancel = cancel;
    result.always(() => { release(); for (const stop of [...stages]) stop(); busy(false); });

    // Replay is explicitly attached to a safe stage, never to the whole action.
    function stage(work, replaySafe = false) {
        const settled = $.Deferred();
        let decision, renewedAfter401 = false;
        const stop = () => settled.reject(cancellation());
        stages.add(stop);
        settled.always(() => { stages.delete(stop); decision?.cancel(); });
        function attempt() {
            if (settled.state() !== 'pending') return;
            if (!current()) return stop();
            busy(true);
            let request;
            try { request = work(); } catch (error) { settled.reject(error); return; }
            $.when(request).then((...values) => {
                if (settled.state() !== 'pending') return;
                if (!replaySafe && options.adoptsAuthority && values[0]?.ReturnCode > 0 && security.incarnationMatches(owner)) authority = security.captureProof();
                if (current()) settled.resolve(...values); else stop();
            }).fail(error => {
                if (settled.state() !== 'pending') return;
                // The refresh denial itself invalidates authority. Its originating
                // live caller still needs the terminal error, not a user Cancel.
                if (owned() && security.isCurrentRenewalDenial?.(authority, error)) return settled.reject(error);
                if (!current() || error?.cancelled) return stop();
                // A protected GET crossing expiry may renew once silently. A
                // second denial is an actual denied action, not a retry loop.
                if (replaySafe && error?.authenticationFailed && error.protectedDispatched && !renewedAfter401) {
                    renewedAfter401 = true;
                    stage(() => security.ensureFresh(), true).then(attempt).fail(settled.reject);
                    return;
                }
                if (options.interactive === false || !recoverable(error) || (!replaySafe && error.protectedDispatched !== false)) return settled.reject(error);
                busy(false);
                decision = wait(error, () => busy(true));
                decision.then(() => {
                    decision = null;
                    if (!current()) return stop();
                    busy(true);
                    attempt();
                }).fail(settled.reject);
            });
        }
        attempt();
        const promise = settled.promise();
        promise.cancel = stop;
        return promise;
    }
    const operation = { current, cancel, read: work => stage(work, true), write: work => stage(work, false) };
    busy(true);
    const ready = options.fresh === false ? $.Deferred().resolve().promise() : stage(() => security.ensureFresh(), true);
    ready.then(() => {
        if (!current()) return cancel();
        let work;
        try { work = action(operation); } catch (error) { result.reject(error); return; }
        $.when(work).then((...values) => current() ? result.resolve(...values) : cancel()).fail(result.reject);
    }).fail(result.reject);
    return promise;
}

function createOwner() {
    const pending = new Map();
    let retired = false;
    return {
        isActive: () => !retired,
        run(action, options = {}) {
            if (retired) return $.Deferred().reject(cancellation()).promise();
            const key = options.key || action;
            if (pending.has(key)) return pending.get(key);
            const request = run(action, { ...options, isCurrent: () => !retired && (!options.isCurrent || options.isCurrent()) });
            pending.set(key, request);
            request.always(() => { if (pending.get(key) === request) pending.delete(key); });
            return request;
        },
        cancel() { retired = true; for (const request of [...pending.values()]) request.cancel(); }
    };
}
const sessionRecovery = { run, wait, recoverable, createOwner };
export default sessionRecovery;
